import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getInitials } from '@vanilla/utils/avatar.js';
import { USER_DEPARTMENTS } from '@vanilla/constants/UserDepartments.js';
import { AuthService } from '@vanilla/services/AuthService.js';
import { UserService } from '@vanilla/services/UserService.js';
import { useAuth } from '../context/AuthContext.jsx';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/components/ui/dialog';
import { sanitizeUrl } from '../utils/sanitize.js';

function profileUserFromSession(session) {
    return {
        id: session.userId,
        name: session.name || '',
        email: session.email || '',
        role: session.role || 'viewer',
        department: session.department || '',
        status: session.status || 'active',
        avatarUrl: session.avatarUrl || '',
    };
}

export default function ProfilePopover({ open, onClose }) {
    const { logout } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('—');
    const [department, setDepartment] = useState('—');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState('');
    const [pendingAvatarUrl, setPendingAvatarUrl] = useState(null);
    const [pendingFile, setPendingFile] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [original, setOriginal] = useState({ name: '', avatarUrl: '' });
    const [saving, setSaving] = useState(false);
    const nameRef = useRef(null);
    const fileRef = useRef(null);

    const loadCurrentUser = useCallback(async () => {
        const session = AuthService.getSession();
        if (!session?.userId) return;
        let user;
        try {
            user = await UserService.getById(session.userId);
        } catch {
            user = null;
        }
        user = user || profileUserFromSession(session);
        const nextOriginal = {
            name: user.name || session.name || '',
            avatarUrl: user.avatarUrl || session.avatarUrl || '',
        };
        setOriginal(nextOriginal);
        setPendingAvatarUrl(null);
        setPendingFile(null);
        if (objectUrl) { URL.revokeObjectURL(objectUrl); setObjectUrl(null); }
        if (fileRef.current) fileRef.current.value = '';
        setName(nextOriginal.name);
        setEmail(user.email || session.email || '—');
        setDepartment(USER_DEPARTMENTS[user.department] || user.department || '—');
        setAvatarUrl(nextOriginal.avatarUrl);
    }, []);

    useEffect(() => {
        if (!open) return;
        loadCurrentUser().then(() => {
            requestAnimationFrame(() => nameRef.current?.focus());
        });
    }, [open, loadCurrentUser]);

    useEffect(() => {
        if (!open) return;
        const onAuth = () => {
            if (!saving) loadCurrentUser();
        };
        window.addEventListener('authChanged', onAuth);
        return () => window.removeEventListener('authChanged', onAuth);
    }, [open, saving, loadCurrentUser]);

    const displayAvatar = pendingAvatarUrl ?? resolvedAvatarUrl;
    const dirty = name.trim() !== original.name || pendingFile !== null;

    useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);

    // Resolve API avatar path to authenticated blob URL (img can't send Bearer)
    useEffect(() => {
        let cancelled = false;
        let blobUrl = null;
        const resolve = async () => {
            if (resolvedAvatarUrl && resolvedAvatarUrl.startsWith('blob:')) {
                try { URL.revokeObjectURL(resolvedAvatarUrl); } catch {}
                setResolvedAvatarUrl('');
            }
            if (!avatarUrl || avatarUrl.startsWith('blob:') || avatarUrl.startsWith('data:')) {
                setResolvedAvatarUrl(avatarUrl || '');
                return;
            }
            // Only fetch if looks like /api/.../avatar
            if (!avatarUrl.includes('/avatar')) {
                setResolvedAvatarUrl('');
                return;
            }
            try {
                const session = AuthService.getSession();
                const uid = session?.userId;
                if (!uid) return;
                const { UsersApiService: Svc } = await import('@vanilla/services/crm/UsersApiService.js');
                const res = await Svc.fetchAvatarBlob(uid);
                if (cancelled) return;
                if (!res?.blob) { setResolvedAvatarUrl(''); return; }
                blobUrl = URL.createObjectURL(res.blob);
                setResolvedAvatarUrl(blobUrl);
            } catch {
                if (!cancelled) setResolvedAvatarUrl('');
            }
        };
        resolve();
        return () => { cancelled = true; if (blobUrl) try { URL.revokeObjectURL(blobUrl); } catch {} };
    }, [avatarUrl]);

    const onAvatarSelected = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowed.includes(file.type)) {
            alert('Only JPEG, PNG, or WebP allowed');
            e.target.value = '';
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            alert('File too large — max 2 MB');
            e.target.value = '';
            return;
        }
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        const url = URL.createObjectURL(file);
        setObjectUrl(url);
        setPendingAvatarUrl(url);
        setPendingFile(file);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const session = AuthService.getSession();
        if (!session?.userId || saving) return;
        const nextName = name.trim();
        if (!nextName) {
            nameRef.current?.focus();
            return;
        }
        setSaving(true);
        try {
            const base = profileUserFromSession(session);
            let existing = null;
            try {
                existing = await UserService.getById(session.userId);
            } catch {
                existing = null;
            }
            // Upload avatar first if changed (DB-backed, not DataURL)
            if (pendingFile) {
                const { UsersApiService } = await import('@vanilla/services/crm/UsersApiService.js');
                await UsersApiService.uploadAvatar(pendingFile);
            }
            const updated = await UserService.save({
                ...(existing || base),
                id: session.userId,
                email: (existing || base).email || session.email,
                role: (existing || base).role || session.role,
                name: nextName,
            });
            // Force avatar URL refresh with cache-bust
            const freshAvatar = pendingFile ? `/api/v1/users/${session.userId}/avatar?t=${Date.now()}` : (updated.avatarUrl || existing?.avatarUrl || session.avatarUrl || '');
            onClose();
            if (objectUrl) { URL.revokeObjectURL(objectUrl); setObjectUrl(null); }
            setPendingFile(null);
            setPendingAvatarUrl(null);
            if (fileRef.current) fileRef.current.value = '';
            AuthService.refreshSession({
                ...updated,
                id: session.userId,
                name: nextName,
                email: updated.email || session.email,
                role: updated.role || session.role,
                department: updated.department || session.department || '',
                status: updated.status || session.status || 'active',
                avatarUrl: freshAvatar,
                hasAvatar: !!pendingFile || !!updated.hasAvatar || !!existing?.hasAvatar,
            });
        } catch (err) {
            console.error('Profile save failed:', err);
            alert(err?.message || 'Could not save profile changes.');
        } finally {
            setSaving(false);
        }
    };

    const handleSignOut = async () => {
        onClose({ returnFocus: false });
        await logout();
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && !saving) onClose();
            }}
        >
            <DialogContent
                unstyled
                hideClose
                id="profilePopover"
                className="profile-popover"
                overlayClassName="profile-popover-backdrop is-visible"
                aria-labelledby="profilePopoverTitle"
                onEscapeKeyDown={(e) => {
                    if (saving) e.preventDefault();
                }}
                onPointerDownOutside={(e) => e.preventDefault()}
                onFocusOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <button type="button" className="profile-popover-close" aria-label="Close profile" onClick={() => onClose()} disabled={saving}>
                    &times;
                </button>
                <form id="profilePopoverForm" className="profile-popover-form" noValidate onSubmit={handleSave}>
                    <div className="profile-popover-avatar-block">
                        <div className={`profile-avatar-wrap${displayAvatar ? ' has-photo' : ''}`} id="profileAvatarWrap">
                            <Avatar className="h-[72px] w-[72px]" id="profileAvatar">
                                {displayAvatar ? (
                                    <AvatarImage id="profileAvatarImg" className="profile-avatar-img" alt="" src={sanitizeUrl(displayAvatar) || undefined} />
                                ) : null}
                                <AvatarFallback
                                    id="profileAvatarInitials"
                                    className="profile-avatar-initials bg-transparent text-[1.25rem] font-semibold text-[var(--text-on-brand)]"
                                >
                                    {getInitials(name)}
                                </AvatarFallback>
                            </Avatar>
                            <button
                                type="button"
                                className="profile-avatar-edit"
                                aria-label="Change profile photo"
                                onClick={() => fileRef.current?.click()}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                                    <path d="M8.5 1.5l2 2L4 10H2v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            id="profileAvatarFile"
                            className="sr-only"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={onAvatarSelected}
                        />
                    </div>
                    <DialogTitle unstyled id="profilePopoverTitle" className="profile-popover-title">My profile</DialogTitle>
                    <div className="form-group">
                        <Label unstyled htmlFor="profileNameInput">Name</Label>
                        <Input
                            unstyled
                            ref={nameRef}
                            type="text"
                            id="profileNameInput"
                            className="form-input"
                            autoComplete="name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={saving}
                        />
                    </div>
                    <div className="form-group">
                        <Label unstyled>Email</Label>
                        <p id="profileEmailDisplay" className="profile-email-readonly">{email}</p>
                    </div>
                    <div className="form-group">
                        <Label unstyled>Department</Label>
                        <p id="profileDeptDisplay" className="profile-email-readonly">{department}</p>
                    </div>
                    <Button
                        type="submit"
                        id="profileSaveBtn"
                        className={`btn btn-primary profile-save-btn${dirty ? '' : ' hidden'}`} variant="default"
                        disabled={!dirty || saving}
                    >
                        {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                    <div className="profile-popover-actions">
                        <Button type="button" className="btn btn-secondary profile-sign-out-btn" variant="secondary" id="signOutBtn" onClick={handleSignOut}>
                            Sign out
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
